import { createTheme, type MantineColorsTuple, type MantineThemeOverride } from "@mantine/core";

import { mix } from "@/branding/color";

/**
 * Mantine theme. Stock defaults per ADR-0041 Q4 (light brand layer deferred
 * to v1.5). Dark-mode default is wired in `main.tsx` via
 * `<MantineProvider defaultColorScheme="dark">` per the readiness doc Q2.
 *
 * This is the Medicus theme: Medicus staff always see it, and so does a lab user whose
 * organisation has no colours of its own.
 */
export const theme = createTheme({
  primaryColor: "blue",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
  defaultRadius: "md",
});

/** The palette name a lab's colours are registered under. Never a status colour's name. */
export const LAB_COLOR = "lab";

/**
 * The ten shades Mantine needs, built from the lab's two colours (plan B5, D4).
 *
 * Shade 6 is the one Mantine fills buttons with and colours links in, so it is the **ink**,
 * which the server holds to 4.5:1 on white: white button text on it passes the same test.
 * The primary sits at shade 5 with its tints above, for borders and light backgrounds, and
 * the ink darkens below for hover.
 */
export function labPalette(primary: string, ink: string): MantineColorsTuple {
  return [
    mix(primary, "#ffffff", 0.9),
    mix(primary, "#ffffff", 0.78),
    mix(primary, "#ffffff", 0.62),
    mix(primary, "#ffffff", 0.45),
    mix(primary, "#ffffff", 0.25),
    primary,
    ink,
    mix(ink, "#000000", 0.15),
    mix(ink, "#000000", 0.3),
    mix(ink, "#000000", 0.45),
  ];
}

/**
 * A lab user's portal theme: the Medicus theme with the lab's palette as the primary colour.
 *
 * **Only the primary colour changes.** The status colours the portal draws badges and
 * alerts in (green approved, orange held, red failed, gray, yellow) are Mantine's named
 * colours, and this override does not touch them, so a lab whose primary is red still sees
 * a failed delivery in the same red as every other lab, and its approved badge stays green.
 */
export function labTheme(primary: string, ink: string): MantineThemeOverride {
  return createTheme({
    ...theme,
    colors: { [LAB_COLOR]: labPalette(primary, ink) },
    primaryColor: LAB_COLOR,
    primaryShade: 6,
  });
}
